// 计算合理的刻度间隔
const calculateNiceInterval = (maxValue, targetTicks = 5) => {
  const rawInterval = Math.abs(maxValue) / targetTicks;
  const magnitude = Math.floor(Math.log10(rawInterval));
  const normalizedInterval = rawInterval / Math.pow(10, magnitude);

  let niceInterval;
  if (normalizedInterval <= 1) niceInterval = 1;
  else if (normalizedInterval <= 2) niceInterval = 2;
  else if (normalizedInterval <= 5) niceInterval = 5;
  else niceInterval = 10;

  return niceInterval * Math.pow(10, magnitude);
};

// 生成合理的刻度点
const generateNiceTickMarks = (minVal, maxVal, isNegativeSupported = false) => {
  if (isNegativeSupported && minVal < 0) {
    // 对于有负值的情况，以0为中心对称
    const maxAbs = Math.max(Math.abs(minVal), Math.abs(maxVal));
    const interval = calculateNiceInterval(maxAbs, 3); // 3个间隔，总共7个点(-3, -2, -1, 0, 1, 2, 3)
    const ticks = [];
    for (let i = -3; i <= 3; i++) {
      ticks.push(i * interval);
    }
    return ticks;
  } else {
    // 对于只有正值的情况
    const interval = calculateNiceInterval(maxVal, 5);
    const ticks = [];
    for (let i = 0; i <= 5; i++) {
      ticks.push(i * interval);
    }
    return ticks;
  }
};

import React, { useState, useEffect, useCallback } from "react";
import { Play, Pause, RotateCcw, Settings, Plus, Trash2 } from "lucide-react";

const MotionSimulator = () => {
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // 多阶段运动设置
  const [stages, setStages] = useState([
    { id: 1, velocity: 10, duration: 3, color: "#3B82F6" },
    { id: 2, velocity: -5, duration: 2, color: "#10B981" },
    { id: 3, velocity: 15, duration: 3, color: "#F59E0B" },
  ]);

  // 单位设置
  const [units, setUnits] = useState({
    distance: "m",
    velocity: "m/s",
    time: "s",
  });

  const unitOptions = {
    distance: ["m", "km", "cm", "mm"],
    velocity: ["m/s", "km/h", "cm/s"],
    time: ["s", "min", "h"],
  };

  // 计算总时间和当前状态
  const totalTime = stages.reduce((sum, stage) => sum + stage.duration, 0);

  const getCurrentState = useCallback(
    (time) => {
      let accumulatedTime = 0;
      let accumulatedDistance = 0;

      for (let i = 0; i < stages.length; i++) {
        const stage = stages[i];
        if (time <= accumulatedTime + stage.duration) {
          const timeInStage = time - accumulatedTime;
          return {
            stageIndex: i,
            velocity: stage.velocity,
            displacement: accumulatedDistance + stage.velocity * timeInStage,
            stageColor: stage.color,
          };
        }
        accumulatedDistance += stage.velocity * stage.duration;
        accumulatedTime += stage.duration;
      }

      return {
        stageIndex: stages.length - 1,
        velocity: 0,
        displacement: accumulatedDistance,
        stageColor: stages[stages.length - 1]?.color || "#3B82F6",
      };
    },
    [stages]
  );

  const currentState = getCurrentState(currentTime);

  // 动画控制
  useEffect(() => {
    let intervalId;
    if (isPlaying && currentTime < totalTime) {
      intervalId = setInterval(() => {
        setCurrentTime((prev) => {
          const next = prev + 0.1;
          return next > totalTime ? totalTime : next;
        });
      }, 100);
    }
    return () => clearInterval(intervalId);
  }, [isPlaying, currentTime, totalTime]);

  // 重置动画
  const resetAnimation = () => {
    setCurrentTime(0);
    setIsPlaying(false);
  };

  // 添加阶段
  const addStage = () => {
    const newId = Math.max(...stages.map((s) => s.id)) + 1;
    const colors = [
      "#3B82F6",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#8B5CF6",
      "#F97316",
    ];
    setStages([
      ...stages,
      {
        id: newId,
        velocity: 10,
        duration: 3,
        color: colors[stages.length % colors.length],
      },
    ]);
  };

  // 删除阶段
  const removeStage = (id) => {
    if (stages.length > 1) {
      setStages(stages.filter((stage) => stage.id !== id));
    }
  };

  // 更新阶段
  const updateStage = (id, field, value) => {
    setStages(
      stages.map((stage) =>
        stage.id === id ? { ...stage, [field]: Number(value) } : stage
      )
    );
  };

  // 生成图表数据
  const generateChartData = useCallback(() => {
    const points = [];
    const steps = Math.max(200, totalTime * 20); // 更高精度

    for (let i = 0; i <= steps; i++) {
      const t = (totalTime * i) / steps;
      const state = getCurrentState(t);
      points.push({
        t,
        s: state.displacement,
        v: state.velocity,
        stageIndex: state.stageIndex,
      });
    }
    return points;
  }, [totalTime, getCurrentState]);

  const chartData = generateChartData();
  const minDisplacement = Math.min(...chartData.map((d) => d.s));
  const maxDisplacement = Math.max(...chartData.map((d) => d.s));
  const displacementRange = maxDisplacement - minDisplacement;
  const minVelocity = Math.min(...stages.map((s) => s.velocity));
  const maxVelocity = Math.max(...stages.map((s) => s.velocity));
  const velocityRange =
    Math.max(Math.abs(minVelocity), Math.abs(maxVelocity)) * 1.1;

  // 优化的图表组件
  const Chart = ({
    data,
    xKey,
    yKey,
    title,
    xLabel,
    yLabel,
    maxY,
    showStages = false,
  }) => {
    const width = 100; // 使用百分比宽度
    const height = 350;
    const padding = 60;

    const maxX = totalTime;

    // 对于v-t图和s-t图，都可能需要支持负值
    const isVelocityChart = yKey === "v";
    const isDisplacementChart = yKey === "s";
    const hasNegativeValues =
      isVelocityChart || (isDisplacementChart && minDisplacement < 0);

    const yMin = hasNegativeValues ? -maxY : 0;
    const yMax = maxY;
    const yRange = yMax - yMin;

    // 计算合理的刻度点
    const xTicks = generateNiceTickMarks(0, maxX);
    const yTicks = hasNegativeValues
      ? generateNiceTickMarks(-maxY, maxY, true)
      : generateNiceTickMarks(0, maxY);

    const xScale = (val, chartWidth) =>
      padding + (val / Math.max(...xTicks)) * (chartWidth - 2 * padding);
    const yScale = (val) => {
      if (hasNegativeValues) {
        const yTickRange = Math.max(...yTicks) - Math.min(...yTicks);
        return (
          height -
          padding -
          ((val - Math.min(...yTicks)) / yTickRange) * (height - 2 * padding)
        );
      } else {
        return (
          height -
          padding -
          (val / Math.max(...yTicks)) * (height - 2 * padding)
        );
      }
    };

    // 分段绘制线条
    const renderSegments = (chartWidth) => {
      let segments = [];
      let currentSegment = [];
      let currentStage = -1;

      data.forEach((d, i) => {
        if (d.stageIndex !== currentStage) {
          if (currentSegment.length > 1) {
            segments.push({
              points: currentSegment,
              stageIndex: currentStage,
            });
          }
          currentSegment = [d];
          currentStage = d.stageIndex;
        } else {
          currentSegment.push(d);
        }
      });

      if (currentSegment.length > 1) {
        segments.push({
          points: currentSegment,
          stageIndex: currentStage,
        });
      }

      return segments.map((segment, i) => {
        const pathData = segment.points
          .map(
            (d, idx) =>
              `${idx === 0 ? "M" : "L"} ${xScale(d[xKey], 600)} ${yScale(
                d[yKey]
              )}`
          )
          .join(" ");

        const color = stages[segment.stageIndex]?.color || "#3B82F6";

        return (
          <path
            key={i}
            d={pathData}
            fill="none"
            stroke={color}
            strokeWidth="3"
            opacity="0.8"
          />
        );
      });
    };

    // 绘制速度跳跃的垂直虚线
    const renderVerticalJumpLines = (chartWidth) => {
      if (yKey !== "v") return null;

      let lines = [];
      let accumulatedTime = 0;

      for (let i = 0; i < stages.length - 1; i++) {
        accumulatedTime += stages[i].duration;
        const currentVelocity = stages[i].velocity;
        const nextVelocity = stages[i + 1].velocity;

        if (currentVelocity !== nextVelocity) {
          lines.push(
            <line
              key={i}
              x1={xScale(accumulatedTime, chartWidth)}
              y1={yScale(currentVelocity)}
              x2={xScale(accumulatedTime, chartWidth)}
              y2={yScale(nextVelocity)}
              stroke="#6B7280"
              strokeWidth="2"
              strokeDasharray="5,5"
              opacity="0.7"
            />
          );
        }
      }

      return lines;
    };

    return (
      <div className="bg-white p-4 rounded-lg shadow-lg w-full">
        <h3 className="text-xl font-bold mb-3 text-center text-gray-800">
          {title}
        </h3>
        <div className="w-full overflow-x-auto">
          <svg
            width="100%"
            height={height}
            viewBox={`0 0 600 ${height}`}
            className="border-2 border-gray-200 min-w-full"
          >
            <defs>
              {/* 缩小的箭头标记 */}
              <marker
                id="arrowhead"
                markerWidth="6"
                markerHeight="4"
                refX="5"
                refY="2"
                orient="auto"
              >
                <polygon points="0 0, 6 2, 0 4" fill="#374151" />
              </marker>
            </defs>

            {/* 网格线 - 基于实际刻度 */}
            {xTicks.map((tick, i) => (
              <line
                key={`x-${i}`}
                x1={xScale(tick, 600)}
                y1={padding}
                x2={xScale(tick, 600)}
                y2={height - padding}
                stroke={tick === 0 ? "#D1D5DB" : "#F3F4F6"}
                strokeWidth={tick === 0 ? "1" : "0.5"}
              />
            ))}
            {yTicks.map((tick, i) => (
              <line
                key={`y-${i}`}
                x1={padding}
                y1={yScale(tick)}
                x2={600 - padding}
                y2={yScale(tick)}
                stroke={tick === 0 ? "#D1D5DB" : "#F3F4F6"}
                strokeWidth={tick === 0 ? "1" : "0.5"}
              />
            ))}

            {/* 坐标轴（带缩小的箭头） */}
            {/* X轴：对于有负值的图，放在y=0位置；否则放在底部 */}
            <line
              x1={padding}
              y1={hasNegativeValues ? yScale(0) : height - padding}
              x2={600 - padding + 10}
              y2={hasNegativeValues ? yScale(0) : height - padding}
              stroke="#374151"
              strokeWidth="3"
              markerEnd="url(#arrowhead)"
            />
            {/* Y轴 */}
            <line
              x1={padding}
              y1={height - padding}
              x2={padding}
              y2={padding - 10}
              stroke="#374151"
              strokeWidth="3"
              markerEnd="url(#arrowhead)"
            />

            {/* X轴刻度标记线 */}
            {xTicks.map((tick, i) => (
              <line
                key={`x-tick-${i}`}
                x1={xScale(tick, 600)}
                y1={hasNegativeValues ? yScale(0) - 4 : height - padding - 4}
                x2={xScale(tick, 600)}
                y2={hasNegativeValues ? yScale(0) + 4 : height - padding + 4}
                stroke="#374151"
                strokeWidth="2"
              />
            ))}

            {/* Y轴刻度标记线 */}
            {yTicks.map((tick, i) => (
              <line
                key={`y-tick-${i}`}
                x1={padding - 4}
                y1={yScale(tick)}
                x2={padding + 4}
                y2={yScale(tick)}
                stroke="#374151"
                strokeWidth="2"
              />
            ))}

            {/* 分段曲线 */}
            {renderSegments(600)}

            {/* 速度跳跃的垂直虚线 */}
            {renderVerticalJumpLines(600)}

            {/* 当前时间点 */}
            <circle
              cx={xScale(currentTime, 600)}
              cy={yScale(
                data.find((d) => Math.abs(d.t - currentTime) < 0.1)?.[yKey] || 0
              )}
              r="6"
              fill="#EF4444"
              stroke="#FFFFFF"
              strokeWidth="2"
            />

            {/* 坐标轴标签 */}
            <text
              x={300}
              y={hasNegativeValues ? yScale(0) + 40 : height - 10}
              textAnchor="middle"
              className="text-sm font-semibold fill-gray-700"
            >
              {xLabel}
            </text>
            <text
              x={20}
              y={height / 2}
              textAnchor="middle"
              transform={`rotate(-90 20 ${height / 2})`}
              className="text-sm font-semibold fill-gray-700"
            >
              {yLabel}
            </text>

            {/* 合理的数值标签 */}
            {xTicks.map((tick, i) => (
              <text
                key={`x-label-${i}`}
                x={
                  xScale(tick, 600) + (hasNegativeValues && tick === 0 ? 8 : 0)
                }
                y={hasNegativeValues ? yScale(0) + 20 : height - padding + 15}
                textAnchor="middle"
                className="text-xs fill-gray-600"
              >
                {Math.round(tick)}
              </text>
            ))}
            {yTicks.map((tick, i) => (
              <text
                key={`y-label-${i}`}
                x={padding - 10}
                y={yScale(tick) + 4}
                textAnchor="end"
                className="text-xs fill-gray-600"
              >
                {Math.round(tick)}
              </text>
            ))}
          </svg>
        </div>
      </div>
    );
  };

  // 计算演示区域的刻度和物体位置
  const getDemoDisplayData = () => {
    // 生成刻度点
    const demoTicks =
      displacementRange > 0
        ? generateNiceTickMarks(
            minDisplacement,
            Math.max(minDisplacement + displacementRange, maxDisplacement),
            minDisplacement < 0
          )
        : [0, 10, 20, 30, 40, 50];

    const ticksToShow = demoTicks.slice(0, 6);

    // 找到0点的索引
    const zeroIndex = ticksToShow.findIndex((tick) => tick === 0);
    const hasZeroTick = zeroIndex !== -1;

    // 计算可用的显示区域（留出边距）
    const leftMargin = 10; // 10%
    const rightMargin = 15; // 15%
    const availableWidth = 100 - leftMargin - rightMargin; // 75%

    // 计算刻度范围
    const tickMin = Math.min(...ticksToShow);
    const tickMax = Math.max(...ticksToShow);
    const tickRange = tickMax - tickMin;

    // 计算物体位置
    let objectPosition;
    if (tickRange === 0) {
      objectPosition = leftMargin + availableWidth / 2; // 居中
    } else {
      // 将当前位移映射到刻度范围内
      const normalizedPosition =
        (currentState.displacement - tickMin) / tickRange;
      objectPosition = leftMargin + normalizedPosition * availableWidth;
      // 限制在边界内
      objectPosition = Math.max(
        leftMargin,
        Math.min(leftMargin + availableWidth, objectPosition)
      );
    }

    return {
      ticks: ticksToShow,
      objectPosition,
      tickMin,
      tickMax,
      leftMargin,
      availableWidth,
    };
  };

  const demoData = getDemoDisplayData();

  return (
    <div className="max-w-7xl mx-auto p-4 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      <h1 className="text-4xl font-bold text-center mb-6 text-indigo-800">
        多阶段机械运动模拟器
      </h1>

      {/* 运动演示区域 - 始终独立显示 */}
      <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
        <h2 className="text-xl font-semibold mb-4">运动演示</h2>
        <div className="relative h-40 bg-gradient-to-b from-sky-200 to-green-200 rounded-lg overflow-hidden">
          {/* 地面 */}
          <div className="absolute bottom-0 w-full h-8 bg-amber-800"></div>

          {/* 刻度线 - 修复位置对齐 */}
          {demoData.ticks.map((tick, i) => {
            const tickPosition =
              demoData.leftMargin +
              (i / (demoData.ticks.length - 1)) * demoData.availableWidth;
            return (
              <div
                key={i}
                className="absolute bottom-8 h-6 border-l-2 border-gray-600"
                style={{ left: `${tickPosition}%` }}
              >
                <span className="absolute -top-6 -left-4 text-xs font-semibold text-gray-700 bg-white px-1 rounded">
                  {Math.round(tick)}
                </span>
              </div>
            );
          })}

          {/* 运动方块 - 修复位置对齐 */}
          <div
            className="absolute bottom-8 w-8 h-8 rounded transform transition-all duration-100 ease-linear shadow-lg"
            style={{
              left: `${demoData.objectPosition}%`,
              backgroundColor: currentState.stageColor,
              transform: "translateX(-50%)", // 让方块中心对齐刻度线
            }}
          >
            {/* 位置标注 */}
            <div className="absolute -top-12 -left-8 text-xs bg-black text-white px-2 py-1 rounded whitespace-nowrap">
              位置: {Math.round(currentState.displacement * 10) / 10}{" "}
              {units.distance}
            </div>

            {/* 速度标注 - 修复位置，放在右侧 */}
            <div className="absolute -top-6 -right-20 text-xs bg-blue-600 text-white px-2 py-1 rounded whitespace-nowrap">
              {currentState.velocity} {units.velocity}
            </div>
          </div>
        </div>

        <p className="text-center mt-4 text-sm text-gray-600">
          彩色方块在地面上运动，颜色对应不同运动阶段
        </p>

        {/* 阶段图例 */}
        <div className="mt-4 flex flex-wrap gap-2">
          {stages.map((stage, index) => (
            <div key={stage.id} className="flex items-center space-x-1">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: stage.color }}
              ></div>
              <span className="text-xs">
                阶段{index + 1}: {stage.velocity}
                {units.velocity}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 控制面板 */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Settings className="mr-2" size={20} />
            运动阶段设置
          </h2>

          {/* 阶段列表 */}
          <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
            {stages.map((stage, index) => (
              <div key={stage.id} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">阶段 {index + 1}</span>
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: stage.color }}
                    ></div>
                    {stages.length > 1 && (
                      <button
                        onClick={() => removeStage(stage.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-gray-600">
                      速度: {stage.velocity} {units.velocity}
                    </label>
                    <input
                      type="range"
                      min="-30"
                      max="30"
                      value={stage.velocity}
                      onChange={(e) =>
                        updateStage(stage.id, "velocity", e.target.value)
                      }
                      className="w-full h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-600">
                      持续时间: {stage.duration} {units.time}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      step="0.5"
                      value={stage.duration}
                      onChange={(e) =>
                        updateStage(stage.id, "duration", e.target.value)
                      }
                      className="w-full h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={addStage}
            className="w-full bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg flex items-center justify-center mb-4"
          >
            <Plus size={18} />
            <span className="ml-1">添加阶段</span>
          </button>

          {/* 时间控制条 */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              当前时间: {Math.round(currentTime * 10) / 10} {units.time} /{" "}
              {Math.round(totalTime * 10) / 10} {units.time}
            </label>
            <input
              type="range"
              min="0"
              max={totalTime}
              step="0.1"
              value={currentTime}
              onChange={(e) => setCurrentTime(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* 单位设置 */}
          <div className="mb-4">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="text-sm text-indigo-600 hover:text-indigo-800 mb-2"
            >
              {showSettings ? "隐藏" : "显示"}单位设置
            </button>

            {showSettings && (
              <div className="p-3 bg-gray-50 rounded space-y-2">
                {Object.entries(unitOptions).map(([key, options]) => (
                  <div key={key} className="flex items-center justify-between">
                    <label className="text-xs font-medium text-gray-600">
                      {key === "distance"
                        ? "距离"
                        : key === "velocity"
                        ? "速度"
                        : "时间"}
                      :
                    </label>
                    <select
                      value={units[key]}
                      onChange={(e) =>
                        setUnits((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      className="text-xs border rounded px-2 py-1"
                    >
                      {options.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 控制按钮 */}
          <div className="flex space-x-3 mb-4">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center justify-center"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              <span className="ml-2">{isPlaying ? "暂停" : "播放"}</span>
            </button>
            <button
              onClick={resetAnimation}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center"
            >
              <RotateCcw size={20} />
              <span className="ml-2">重置</span>
            </button>
          </div>

          {/* 当前状态显示 */}
          <div className="p-4 bg-indigo-50 rounded-lg">
            <h3 className="font-semibold text-indigo-800 mb-2">当前状态</h3>
            <div className="text-sm space-y-1">
              <p>
                <strong>阶段:</strong> {currentState.stageIndex + 1}/
                {stages.length}
              </p>
              <p>
                <strong>位移 (s):</strong>{" "}
                {Math.round(currentState.displacement * 10) / 10}{" "}
                {units.distance}
              </p>
              <p>
                <strong>速度 (v):</strong> {currentState.velocity}{" "}
                {units.velocity}
              </p>
              <p>
                <strong>时间 (t):</strong> {Math.round(currentTime * 10) / 10}{" "}
                {units.time}
              </p>
            </div>
          </div>
        </div>

        {/* 图表区域 */}
        <div className="space-y-6">
          <Chart
            data={chartData}
            xKey="t"
            yKey="s"
            title={`位移-时间图 (s-t)`}
            xLabel={`时间 (${units.time})`}
            yLabel={`位移 (${units.distance})`}
            maxY={
              Math.max(Math.abs(minDisplacement), Math.abs(maxDisplacement)) *
              1.1
            }
          />
          <Chart
            data={chartData}
            xKey="t"
            yKey="v"
            title={`速度-时间图 (v-t)`}
            xLabel={`时间 (${units.time})`}
            yLabel={`速度 (${units.velocity})`}
            maxY={velocityRange}
          />
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default MotionSimulator;
